import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MapPin, Phone, Mail, MessageCircle, Linkedin } from "lucide-react";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [tsError, setTsError] = useState(false);
  const widgetRef = useRef(null);
  const widgetId = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (!cancelled && widgetRef.current && window.turnstile && !widgetId.current) {
        widgetId.current = window.turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          appearance: "interaction-only",
          callback: (t) => { setTsToken(t); setTsError(false); },
          "expired-callback": () => setTsToken(""),
          "error-callback": () => { setTsToken(""); setTsError(true); },
        });
      }
    };
    tryRender();
    const iv = setInterval(tryRender, 150);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !tsToken) {
      toast.error("Please complete the verification check.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, turnstile_token: tsToken || undefined });
      toast.success("Thank you. Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setTsToken("");
      if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-contact" className="pt-32 min-h-screen bg-[#FAFAF9]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-center gap-3 text-[#A5854C] mb-8">
          <div className="hairline w-12"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Contact</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[44px] md:text-[80px] leading-[1] max-w-4xl"
        >
          Let's discuss your <span className="italic text-[#A5854C]">aviation requirements.</span>
        </motion.h1>
      </section>

      <section className="border-t border-[#0E1B2E]/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-12 py-16 md:py-24">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-6">Head office</div>
            <div className="space-y-6">
              <div className="flex gap-4"><MapPin className="text-[#A5854C] shrink-0 mt-1" size={18} />
                <div>
                  <div className="text-[#0E1B2E] text-[16px]">JS Aviation Group</div>
                  <div className="text-[#6B7684] text-[14px] mt-1 leading-relaxed">
                    Shams Business Centre,<br/> Sharjah Media City Free Zone,<br/> Al Messaned, Sharjah, UAE
                  </div>
                </div>
              </div>
              <div className="flex gap-4"><Phone className="text-[#A5854C] shrink-0 mt-1" size={18} />
                <div className="text-[14px]">
                  <a href="tel:+919999981766" data-testid="contact-phone-in" className="block text-[#0E1B2E] hover:text-[#A5854C]">+91 99999 81766</a>
                  <a href="tel:+447459103218" data-testid="contact-phone-uk" className="block text-[#0E1B2E] hover:text-[#A5854C] mt-1">+44 7459 103218</a>
                </div>
              </div>
              <div className="flex gap-4"><Mail className="text-[#A5854C] shrink-0 mt-1" size={18} />
                <a href="mailto:info@jsgroupholdings.com" className="text-[#0E1B2E] hover:text-[#A5854C] text-[14px] break-all">info@jsgroupholdings.com</a>
              </div>
            </div>

            <div className="mt-10 flex items-center flex-wrap gap-3">
              <a data-testid="contact-whatsapp-in" href="https://wa.me/919999981766" target="_blank" rel="noreferrer" className="btn-ghost px-4 py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2">
                <MessageCircle size={14} /> WhatsApp +91
              </a>
              <a data-testid="contact-whatsapp-uk" href="https://wa.me/447459103218" target="_blank" rel="noreferrer" className="btn-ghost px-4 py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2">
                <MessageCircle size={14} /> WhatsApp +44
              </a>
              <a data-testid="contact-linkedin" href="https://linkedin.com" target="_blank" rel="noreferrer" className="btn-ghost px-4 py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2">
                <Linkedin size={14} /> LinkedIn
              </a>
            </div>

            <div className="mt-14 border border-[#0E1B2E]/10 h-64 relative overflow-hidden">
              <iframe
                title="JS Aviation Office"
                src="https://www.google.com/maps?q=Shams+Business+Centre+Sharjah+Media+City&output=embed"
                className="w-full h-full filter grayscale contrast-110"
                loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="col-span-12 lg:col-span-7 border border-[#0E1B2E]/10 bg-white p-6 md:p-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-8">Consultation request</div>
            <div className="grid grid-cols-2 gap-5">
              {[
                ["name", "Full Name", "text", true],
                ["email", "Email", "email", true],
                ["phone", "Phone", "tel", false],
                ["company", "Company", "text", false],
              ].map(([k, label, type, req]) => (
                <label key={k} className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-2">{label}{req && " *"}</span>
                  <input
                    data-testid={`contact-input-${k}`}
                    type={type}
                    required={req}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors"
                  />
                </label>
              ))}
              <label className="col-span-2 block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-2">Subject</span>
                <select
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none"
                >
                  {["General Inquiry", "Aircraft Acquisition", "Aircraft Sale", "Aircraft Finance", "Fleet Training Aircraft", "Asset Management", "Market Intelligence"].map(s => <option key={s} className="bg-white">{s}</option>)}
                </select>
              </label>
              <label className="col-span-2 block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-2">Message *</span>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none resize-none transition-colors"
                />
              </label>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={submitting || (TURNSTILE_SITE_KEY && !tsToken)}
                data-testid="contact-submit"
                className="btn-gold px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-mono disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
              <div ref={widgetRef} data-testid="contact-turnstile" className="min-h-[2px]"></div>
              {tsError && (
                <span className="text-[#6B7684] text-[12px]" data-testid="contact-turnstile-error">
                  Verification unavailable here — please email <a className="text-[#A5854C] underline" href="mailto:info@jsgroupholdings.com">info@jsgroupholdings.com</a> directly.
                </span>
              )}
              <span className="text-[#9AA3AE] text-[12px]">All inquiries are confidential.</span>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
